import type { Router } from 'vue-router';

/** The only route with offline support (cached day-sheets + outbox) — see views/teaching/presences.vue. */
const PRESENCES_ROUTE = 'enseignement-presences';

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && !navigator.onLine;
}

/**
 * Offline routing lock-down (#377). Présences is the only page that works without a connection
 * (its data is precached in IndexedDB and the app shell is precached by the service worker), so:
 *
 *  - opening the app — or navigating — while offline sends the user to Présences from anywhere else;
 *  - dropping offline on another page moves them to Présences automatically.
 *
 * Online navigation is completely unaffected (the guard is a no-op when `navigator.onLine`).
 * Pairs with the sidebar lock-down in the view (drawer greyed offline); this also covers
 * programmatic navigation, deep links and the browser back/forward buttons.
 */
export function installOfflineRedirect(router: Router): void {
  // Block navigating to a non-offline page while offline (covers the initial load too).
  router.beforeEach((to) => {
    if (isOffline() && to.name !== PRESENCES_ROUTE) {
      return { name: PRESENCES_ROUTE };
    }
    return true;
  });

  // Already sitting on another page when the connection drops → move to Présences.
  if (typeof window !== 'undefined') {
    window.addEventListener('offline', () => {
      if (router.currentRoute.value.name !== PRESENCES_ROUTE) {
        void router.push({ name: PRESENCES_ROUTE });
      }
    });
  }
}
